// Reusable marketplace browser used inside stages.
//
//   <VendorBrowser
//     type="stager"
//     journey={journey}
//     state={state}
//     update={update}
//     stageId="staging"
//   />
//
// Responsibilities:
//   1. Fetch active vendors of `type` (filter by property ZIP when present).
//   2. Render the list; let the seller pick one.
//   3. Auto-request an AI quote for the picked vendor.
//   4. Persist `{ picked_vendor_id, quote, accepted }` into stage data so the
//      orchestrator (a) shows it across navigations, (b) marks the stage
//      `awaiting` while the quote loads, `done` once accepted.

import { useEffect, useState } from "react";
import Spinner from "../components/Spinner";
import VendorCard from "./VendorCard";
import QuoteCard from "./QuoteCard";
import { listVendors, generateQuote, rankVendors, counterQuote, acceptQuote, scheduleBooking } from "./api";
import { track } from "../lib/api";
import { propFactsLine } from "../lib/property";
import { getStageState } from "../lib/journey";

const extractZip = (address) => {
  const m = (address || "").match(/\b(\d{5})(?:-\d{4})?\b/);
  return m?.[1] || null;
};

export default function VendorBrowser({ type, journey, state, update, stageId }) {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteErr, setQuoteErr] = useState(null);
  // AI ranking — { top: [{vendor_id, reason}], loading }
  const [aiPicks, setAiPicks] = useState({ top: [], loading: false });
  // Counter draft modal state
  const [counterFor, setCounterFor] = useState(null); // quote_id being countered
  const [counterText, setCounterText] = useState("");
  const [counterLoading, setCounterLoading] = useState(false);

  const data = state.data || {};
  const pickedId = data.picked_vendor_id || null;
  const quote = data.quote || null;
  const accepted = Boolean(data.accepted_at);
  const quoteId = data.quote_id || null;
  const vendorContact = data.vendor_contact || null;

  // Initial vendor load — skip entirely if vendor already booked.
  useEffect(() => {
    if (accepted) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      setLoading(true); setErr(null);
      try {
        const zip = extractZip(journey.address);
        const rows = await listVendors({ type, zip });
        // If filtered-by-zip returns nothing, fall back to nation-wide.
        const list = rows.length ? rows : await listVendors({ type });
        if (!cancelled) setVendors(list);
      } catch (e) {
        if (cancelled) return;
        // Differentiate between "DB table missing" (migration not run) and a
        // generic load failure so the seller / dev can act on it.
        const d = (e.detail || "").toLowerCase();
        if (d.includes("relation") && d.includes("vendor_profiles")) {
          setErr("Vendor marketplace isn't set up yet. Try again shortly.");
        } else if (e.status === 401) {
          setErr("Not authorized to load vendors — beta token mismatch.");
        } else {
          setErr(`Couldn't load vendors${e.detail ? ` — ${e.detail}` : ""}. Try again.`);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [type, journey.address]); // eslint-disable-line

  // Run AI vendor matching after vendors load.
  useEffect(() => {
    if (accepted || !vendors.length) return;
    let cancelled = false;
    setAiPicks({ top: [], loading: true });
    rankVendors({
      type,
      address: journey.address,
      facts: propFactsLine(journey.property),
      zip: extractZip(journey.address),
    })
      .then((r) => { if (!cancelled) setAiPicks({ top: r.top || [], loading: false }); })
      .catch(() => { if (!cancelled) setAiPicks({ top: [], loading: false }); });
    return () => { cancelled = true; };
  }, [vendors.length, type, journey.address]);

  const requestQuote = async (vendor, note = "") => {
    setQuoteLoading(true); setQuoteErr(null);
    update({
      status: "awaiting",
      data: { ...data, picked_vendor_id: vendor.id, picked_vendor_name: vendor.name, quote: null, accepted_at: null },
    });
    track("vendor_quote_requested", { vendor_id: vendor.id, type, stage_id: stageId, has_note: Boolean(note) });
    try {
      const listing = getStageState(journey, "listing").data || {};
      const valuation = getStageState(journey, "valuation").data || {};
      const res = await generateQuote({
        vendor_id: vendor.id,
        address: journey.address,
        facts: propFactsLine(journey.property),
        listing_headline: listing.headline,
        listing_body: listing.body,
        list_price: valuation.range?.midpoint || null,
        stage_id: stageId,
        note,
      });
      update({
        status: "in_progress",
        data: {
          ...data,
          picked_vendor_id: vendor.id,
          picked_vendor_name: vendor.name,
          quote: res.quote,
          quote_id: res.quote_id,
          accepted_at: null,
        },
      });
      track("vendor_quote_received", { vendor_id: vendor.id, type, total: res.quote?.total });
    } catch (e) {
      setQuoteErr(e.message || "Quote failed.");
      update({ status: "in_progress" });
    } finally {
      setQuoteLoading(false);
    }
  };

  const accept = async () => {
    const acceptedAt = new Date().toISOString();
    update({ status: "done", data: { ...data, accepted_at: acceptedAt } });
    track("vendor_quote_accepted", { vendor_id: pickedId, type, stage_id: stageId });
    if (quoteId) {
      try {
        const r = await acceptQuote({ quote_id: quoteId });
        if (r.contact) {
          update({ status: "done", data: { ...data, accepted_at: acceptedAt, vendor_contact: r.contact } });
        }
      } catch {}
    }
  };

  const schedule = async (scheduledIso, notes) => {
    if (!quoteId) throw new Error("No quote ID");
    await scheduleBooking({ quote_id: quoteId, scheduled_at: scheduledIso, notes });
    update({ status: "done", data: { ...data, scheduled_at: scheduledIso } });
    track("vendor_booking_confirmed", { vendor_id: pickedId, type, stage_id: stageId });
  };

  const draftCounter = async () => {
    if (!quoteId) return;
    setCounterLoading(true); setCounterText("");
    setCounterFor(quoteId);
    track("vendor_counter_requested", { quote_id: quoteId, stage_id: stageId });
    try {
      // Target: 10% under current total, 2 days faster than quoted lead.
      const target_total = quote.total ? Math.round(quote.total * 0.9) : null;
      const target_lead_days = quote.lead_time_days != null ? Math.max(0, quote.lead_time_days - 2) : null;
      const r = await counterQuote({ quote_id: quoteId, target_total, target_lead_days });
      setCounterText(r.counter_text || "");
    } catch (e) {
      setCounterText("Couldn't draft counter — try again.");
    } finally {
      setCounterLoading(false);
    }
  };

  if (accepted && data.picked_vendor_name) {
    return (
      <div className="s-soon">
        <div className="s-soon-row">
          <div className="s-soon-bullet">✓</div>
          <div className="s-soon-text">
            <strong>{data.picked_vendor_name}</strong> is booked for this stage.
          </div>
        </div>
      </div>
    );
  }

  if (loading) return <Spinner label={`Loading ${type}s…`} />;
  if (err) return (
    <div>
      <div className="s-intake-err" style={{ marginBottom: 12 }}>{err}</div>
      <button type="button" className="s-btn amber" onClick={() => { setErr(null); setLoading(true); listVendors({ type }).then(setVendors).catch(() => setErr("Still failing")).finally(() => setLoading(false)); }}>
        Try again
      </button>
    </div>
  );
  if (!vendors.length) return (
    <div style={{ padding: 24, textAlign: "center", color: "#5b7365", fontStyle: "italic", background: "#fafcfb", borderRadius: 12, border: "1px dashed #d8ede6" }}>
      No active {type}s in our marketplace yet. Check back soon.
    </div>
  );

  // Partition vendors into AI-picked vs the rest.
  const pickedIds = new Set(aiPicks.top.map((p) => p.vendor_id));
  const aiPickedVendors = aiPicks.top
    .map((p) => ({ vendor: vendors.find((v) => v.id === p.vendor_id), reason: p.reason }))
    .filter((x) => x.vendor);
  const otherVendors = vendors.filter((v) => !pickedIds.has(v.id));

  return (
    <>
      <div className="s-ai-pill" style={{ marginBottom: 14 }}>
        ★ AI · Top picks ranked for your property + personalized quotes
      </div>

      {/* AI's top picks */}
      {aiPicks.loading && (
        <div style={{ marginBottom: 14 }}>
          <Spinner label={`AI ranking ${type}s for your property…`} />
        </div>
      )}
      {!aiPicks.loading && aiPickedVendors.length > 0 && (
        <>
          <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, color: "#5c4db1", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8 }}>
            AI picks for this property
          </div>
          <div className="s-vendor-grid">
            {aiPickedVendors.map(({ vendor, reason }, i) => (
              <div key={vendor.id} style={{ position: "relative" }}>
                <div style={{ position: "absolute", top: -8, left: 10, background: "#5c4db1", color: "#fff", fontFamily: "'DM Mono',monospace", fontSize: 10, padding: "2px 8px", borderRadius: 8, letterSpacing: "0.06em", zIndex: 1 }}>
                  ★ #{i + 1}
                </div>
                <VendorCard
                  vendor={vendor}
                  selected={vendor.id === pickedId}
                  onSelect={requestQuote}
                />
                {reason && (
                  <div style={{ fontSize: 11, color: "#5c4db1", fontStyle: "italic", marginTop: 6, padding: "0 4px", lineHeight: 1.5 }}>
                    {reason}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Remaining vendors */}
      {otherVendors.length > 0 && (
        <>
          <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, color: "#9aa9a0", letterSpacing: "0.1em", textTransform: "uppercase", margin: "18px 0 8px" }}>
            {aiPickedVendors.length > 0 ? "Other available" : "All available"}
          </div>
          <div className="s-vendor-grid">
            {otherVendors.map((v) => (
              <VendorCard
                key={v.id}
                vendor={v}
                selected={v.id === pickedId}
                onSelect={requestQuote}
              />
            ))}
          </div>
        </>
      )}

      {quoteLoading && (
        <div style={{ marginTop: 14 }}>
          <Spinner label="Generating your personalized quote…" />
        </div>
      )}

      {quoteErr && (
        <div style={{ marginTop: 14 }}>
          <div className="s-intake-err" style={{ marginBottom: 10 }}>{quoteErr}</div>
          <button
            type="button"
            className="s-btn amber"
            onClick={() => {
              const v = vendors.find((x) => x.id === pickedId);
              if (v) requestQuote(v);
            }}
          >Try again</button>
        </div>
      )}

      {quote && (
        <QuoteCard
          quote={quote}
          vendorName={data.picked_vendor_name}
          accepted={accepted}
          vendorContact={vendorContact}
          quoteId={quoteId}
          scheduledAt={data.scheduled_at || null}
          onAccept={accept}
          onSchedule={accepted ? schedule : undefined}
          onRegenerate={(note) => {
            const v = vendors.find((x) => x.id === pickedId);
            if (v) requestQuote(v, note);
          }}
        />
      )}

      {/* AI-drafted counter — appears below the quote card when accepted */}
      {quote && !accepted && quoteId && (
        <div style={{ marginTop: 10, display: "flex", justifyContent: "flex-end" }}>
          <button type="button" className="s-link-btn" onClick={draftCounter} disabled={counterLoading}>
            {counterLoading ? "drafting counter…" : "↳ AI · draft counter offer"}
          </button>
        </div>
      )}

      {counterFor && (counterText || counterLoading) && (
        <div style={{ background: "#fef4e8", border: "1px solid #f1d6a8", borderRadius: 12, padding: 16, marginTop: 12 }}>
          <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, color: "#c47c1a", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8 }}>
            ★ AI · Draft counter email
          </div>
          {counterLoading ? (
            <Spinner label="Drafting…" />
          ) : (
            <>
              <pre style={{ whiteSpace: "pre-wrap", fontFamily: "'Outfit',sans-serif", fontSize: 13, color: "#1c2b25", lineHeight: 1.65, margin: 0 }}>
                {counterText}
              </pre>
              <div style={{ marginTop: 10, display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button type="button" className="s-link-btn" onClick={() => { setCounterFor(null); setCounterText(""); }}>close</button>
                <button type="button" className="s-link-btn" onClick={() => navigator.clipboard?.writeText(counterText)}>copy</button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
